'use client'

import { ArrowLeft24, InfoCircle24, CheckmarkShield12 } from '@vinted/monochrome-icons'
import { Button, Cell, Icon, Navigation, Spacer, Text } from '@vinted/web-ui'

import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { OppositeUser } from '@marketplace-web/domain/conversation-messaging-data'

type Props = {
  onGoBack?: () => void
  onUsernameClick: () => void
  onInfoIconClick: () => void
  isSystem?: boolean
  oppositeUser: OppositeUser | null | undefined
}

const ConversationHeader = ({
  isSystem,
  onGoBack,
  onUsernameClick,
  onInfoIconClick,
  oppositeUser,
}: Props) => {
  const breakpoints = useBreakpoint()
  const translate = useTranslate()
  const inboxTranslate = useTranslate('inbox')

  const getUsername = () => {
    if (isSystem && !oppositeUser?.login) {
      return translate('conversations.list.braze.message_title')
    }

    if (!oppositeUser) {
      return translate('conversations.list.deleted_opposite_user.login')
    }

    return oppositeUser.login
  }

  const getUserProfileUrl = () => {
    if (!oppositeUser) return undefined

    return oppositeUser.profileUrl
  }

  if (oppositeUser === undefined && !isSystem) return null

  const username = getUsername()

  const renderModeratorBadge = () => (
    <>
      <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
      <Icon
        name={CheckmarkShield12}
        color={Icon.Color.Primary}
        aria={{
          'aria-label': inboxTranslate('a11y.moderator_badge'),
        }}
        testId="moderator-badge-icon"
      />
    </>
  )

  const headerTitle =
    isSystem || oppositeUser?.isSystem ? (
      <>
        {username}
        {renderModeratorBadge()}
      </>
    ) : (
      <a
        href={getUserProfileUrl()}
        onClick={onUsernameClick}
        aria-label={inboxTranslate('a11y.user_profile', { username })}
      >
        {username}
      </a>
    )

  return (
    <div className="conversation-header">
      <Navigation
        left={
          breakpoints.portables && (
            <Button
              styling={Button.Styling.Flat}
              theme="amplified"
              inline
              iconName={ArrowLeft24}
              testId="back-button"
              onClick={onGoBack}
            />
          )
        }
        body={
          <Cell styling={Cell.Styling.Tight}>
            <Text as="h2" type={Text.Type.Title} text={headerTitle} theme="amplified" />
          </Cell>
        }
        right={
          <Button
            styling={Button.Styling.Flat}
            theme="amplified"
            inline
            iconName={InfoCircle24}
            testId="details-button"
            onClick={onInfoIconClick}
            aria={{
              'aria-label': inboxTranslate('a11y.information'),
            }}
          />
        }
      />
    </div>
  )
}

export default ConversationHeader
